import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import ProductAttributesEditor from '../../components/ProductAttributesEditor';
import { Product, ProductAttributeField, ProductAttributes, ProductCategory } from '../../types';
import { productService, UpdateProductRequest } from '../../services/productService';
import { attributeService } from '../../services/attributeService';
import { salesItemService } from '../../services/salesItemService';
import { purchaseItemService } from '../../services/purchaseItemService';
import unitService, { Unit } from '../../services/unitService';
import '../PurchaseItems/PurchaseItemPage.css';
import { ShoppingCart, ShoppingBag, Tag, Ruler, ArrowLeft, Save, RefreshCcw } from 'lucide-react';

import { getServerErrorMessage } from '../../utils/errorUtils';
const MAX_LENGTHS = {
  name: 100,
  description: 200,
};

interface FieldErrors {
  name?: string;
  categoryId?: string;
  unitOfMeasure?: string;
  description?: string;
  reorderLevel?: string;
  reorderQuantity?: string;
}

const ProductEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<UpdateProductRequest>({
    name: '',
    description: '',
    categoryId: 0,
    unitOfMeasure: '',
    reorderLevel: 0,
    reorderQuantity: 0,
  });
  const [attrForm, setAttrForm] = useState<ProductAttributes | null>(null);
  const [attrValues, setAttrValues] = useState<Record<number, string>>({});
  const [attrErrors, setAttrErrors] = useState<Record<number, string>>({});
  const [hasSalesItem, setHasSalesItem] = useState<boolean | null>(null);
  const [hasPurchaseItem, setHasPurchaseItem] = useState<boolean | null>(null);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const productId = parseInt(id || '0');
      const [productRes, categoriesRes, unitsRes, attrsRes, salesItemsRes, purchaseItemsRes] = await Promise.all([
        productService.getProductById(productId),
        productService.getCategories({ pageSize: 100 }),
        unitService.getUnits(1, 200),
        attributeService.getProductAttributes(productId).catch(() => null),
        salesItemService.getSalesItemsByProduct(productId).catch(() => null),
        purchaseItemService.getPurchaseItemsByProduct(productId).catch(() => null),
      ]);
      setHasSalesItem(((salesItemsRes?.data as any)?.data?.length ?? 0) > 0);
      setHasPurchaseItem(((purchaseItemsRes?.data as any)?.data?.length ?? 0) > 0);
      if (productRes.data.data) {
        const prod = productRes.data.data as Product;
        setProduct(prod);
        setFormData({
          name: prod.name,
          description: prod.description,
          categoryId: prod.categoryId,
          unitOfMeasure: prod.unitOfMeasure,
          reorderLevel: prod.reorderLevel,
          reorderQuantity: prod.reorderQuantity,
        });
      }
      if (attrsRes?.data?.data) {
        const form = attrsRes.data.data as ProductAttributes;
        setAttrForm(form);
        const values: Record<number, string> = {};
        for (const attr of [...form.moduleAttributes, ...form.customAttributes]) {
          if (attr.value != null) values[attr.attributeDefinitionId] = attr.value;
        }
        setAttrValues(values);
      }
      if (categoriesRes.data.data) {
        setCategories(categoriesRes.data.data as ProductCategory[]);
      }
      if (unitsRes) {
        const unitsArray = Array.isArray(unitsRes) ? unitsRes : (unitsRes as any)?.data;
        if (unitsArray) setUnits(unitsArray);
      }
      setLoading(false);
    } catch {
      setError(t('product.failedToLoadProduct'));
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'reorderLevel' || name === 'reorderQuantity') {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value) || 0 }));
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      return;
    }

    if (name === 'categoryId') {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value) || 0 }));
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      return;
    }

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = t('product.formValidation.productNameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('product.formValidation.productNameMaxLength');
      isValid = false;
    }

    if (!formData.categoryId) {
      errors.categoryId = t('product.formValidation.categoryRequired');
      isValid = false;
    }

    if (!formData.unitOfMeasure) {
      errors.unitOfMeasure = t('product.formValidation.unitOfMeasureRequired');
      isValid = false;
    }

    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('product.formValidation.descriptionMaxLength');
      isValid = false;
    }

    if (formData.reorderLevel < 0) {
      errors.reorderLevel = t('product.formValidation.reorderLevelInvalid');
      isValid = false;
    }

    if (formData.reorderQuantity < 0) {
      errors.reorderQuantity = t('product.formValidation.reorderQuantityInvalid');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  // Custom attributes shown for the currently selected category:
  // general ones always, category-scoped ones only on a category match.
  const visibleCustomAttributes: ProductAttributeField[] = (attrForm?.customAttributes ?? []).filter(
    (a) => a.appliesToAllProducts || (formData.categoryId > 0 && a.categoryIds.includes(formData.categoryId))
  );

  // Values typed for attributes of another category are kept in state (and
  // restored when switching back) but are never saved — surface a notice.
  const hasHiddenValues = (attrForm?.customAttributes ?? []).some(
    (a) => !visibleCustomAttributes.includes(a) && (attrValues[a.attributeDefinitionId] ?? '').trim() !== ''
  );

  const visibleAttributes: ProductAttributeField[] = [
    ...(attrForm?.moduleAttributes ?? []),
    ...visibleCustomAttributes,
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm() || !product) return;

    // Required attributes (module block + applicable custom) must be filled
    const missing: Record<number, string> = {};
    for (const attr of visibleAttributes) {
      if (attr.isRequired && !(attrValues[attr.attributeDefinitionId] ?? '').trim()) {
        missing[attr.attributeDefinitionId] = t('attribute.valueRequired', { name: attr.name });
      }
    }
    setAttrErrors(missing);
    if (Object.keys(missing).length > 0) {
      setError(Object.values(missing)[0]);
      return;
    }

    setSaving(true);
    try {
      await productService.updateProduct(product.id, { ...formData });
      const values = visibleAttributes
        .filter((attr) => (attrValues[attr.attributeDefinitionId] ?? '').trim() !== '')
        .map((attr) => ({
          attributeDefinitionId: attr.attributeDefinitionId,
          value: attrValues[attr.attributeDefinitionId].trim(),
        }));
      await attributeService.setProductAttributes(product.id, { values });
      navigate(`/products/${product.id}`, { state: { message: t('product.productUpdatedSuccess') } });
    } catch (err: any) {
      setError(
        getServerErrorMessage(err) ||
        t('product.failedToUpdateProduct')
      );
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div style={{ padding: '24px' }}>
          <div className="ims-alert ims-alert-error">{error || t('product.productNotFound')}</div>
          <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/products')}>
            {t('product.backToProducts')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <form className="pi-edit-page" onSubmit={handleSubmit}>

        {/* Top Bar */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate(`/products/${product.id}`)}
            >
              <ArrowLeft size={16} strokeWidth={2} />
              <span>{t('product.backToList')}</span>
            </button>
            <div>
              <div className="ims-page-title">{t('product.editTitle')}</div>
              <div className="ims-page-subtitle">{product.name} &mdash; <code>{product.sku}</code></div>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate(`/products/${product.id}`)}
              disabled={saving}
            >
              <RefreshCcw size={15} strokeWidth={1.75} />
              {t('common.cancel')}
            </button>
            <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <span className="ims-btn-spinner" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save size={15} strokeWidth={1.75} />
                  {t('product.saveChanges')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="ims-alert-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
        )}

        {/* Body */}
        <div className="ims-create-body">

          {/* LEFT — Product Identity */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('product.details')}</h2>
              </div>
              <div className="ims-card-body">

                {/* SKU — read-only */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('product.sku')}</label>
                  <input
                    type="text"
                    className="ims-form-control"
                    value={product.sku}
                    readOnly
                    disabled
                  />
                  <small style={{ fontSize: '11px', color: 'var(--ims-text-muted)', marginTop: '3px', display: 'block' }}>{t('product.skuAutoGenerated')}</small>
                </div>

                {/* Product Name */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.productName')} <span className="required">*</span>
                    <span style={{ float: 'right', fontWeight: 400, fontSize: '11px', color: 'var(--ims-text-muted)' }}>{formData.name.length}/{MAX_LENGTHS.name}</span>
                  </label>
                  <input
                    type="text"
                    className={`ims-form-control${fieldErrors.name ? ' is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('product.productName')}
                    maxLength={MAX_LENGTHS.name}
                  />
                  {fieldErrors.name && <span className="pi-field-error">{fieldErrors.name}</span>}
                </div>

                {/* Description */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.description')}
                    <span style={{ float: 'right', fontWeight: 400, fontSize: '11px', color: 'var(--ims-text-muted)' }}>{formData.description.length}/{MAX_LENGTHS.description}</span>
                  </label>
                  <textarea
                    className={`ims-form-control${fieldErrors.description ? ' is-invalid' : ''}`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder={t('product.description')}
                    maxLength={MAX_LENGTHS.description}
                  />
                  {fieldErrors.description && <span className="pi-field-error">{fieldErrors.description}</span>}
                </div>

                {/* Category */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.category')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.categoryId ? ' is-invalid' : ''}`}
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                  >
                    <option value={0}>{t('product.selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {fieldErrors.categoryId && <span className="pi-field-error">{fieldErrors.categoryId}</span>}
                </div>

              </div>
            </div>
          </div>

          {/* CENTER — Inventory Settings */}
          <div className="ims-panel-center">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('product.inventorySettings')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Unit of Measure */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.unitOfMeasure')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.unitOfMeasure ? ' is-invalid' : ''}`}
                    name="unitOfMeasure"
                    value={formData.unitOfMeasure}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('product.selectUnitOfMeasure')}</option>
                    {units.map((unit) => (
                      <option key={unit.unitId} value={unit.unitName}>
                        {unit.unitName} ({unit.unitShortName})
                      </option>
                    ))}
                  </select>
                  {fieldErrors.unitOfMeasure && <span className="pi-field-error">{fieldErrors.unitOfMeasure}</span>}
                </div>

                {/* Reorder Level */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('product.reorderLevel')}</label>
                  <input
                    type="number"
                    className={`ims-form-control${fieldErrors.reorderLevel ? ' is-invalid' : ''}`}
                    name="reorderLevel"
                    value={formData.reorderLevel}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0"
                  />
                  {fieldErrors.reorderLevel && <span className="pi-field-error">{fieldErrors.reorderLevel}</span>}
                  {!fieldErrors.reorderLevel && <small style={{ fontSize: '11px', color: 'var(--ims-text-muted)', marginTop: '3px', display: 'block' }}>{t('product.reorderLevelHelperText')}</small>}
                </div>

                {/* Reorder Quantity */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('product.reorderQuantity')}</label>
                  <input
                    type="number"
                    className={`ims-form-control${fieldErrors.reorderQuantity ? ' is-invalid' : ''}`}
                    name="reorderQuantity"
                    value={formData.reorderQuantity}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0"
                  />
                  {fieldErrors.reorderQuantity && <span className="pi-field-error">{fieldErrors.reorderQuantity}</span>}
                  {!fieldErrors.reorderQuantity && <small style={{ fontSize: '11px', color: 'var(--ims-text-muted)', marginTop: '3px', display: 'block' }}>{t('product.reorderQuantityHelperText')}</small>}
                </div>

              </div>
            </div>

            {/* Module attributes — always visible, seeded by the company's module */}
            {attrForm && attrForm.moduleAttributes.length > 0 && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">
                    {attrForm.moduleSetName || t('attribute.moduleSection')}
                  </h2>
                </div>
                <div className="ims-card-body">
                  <ProductAttributesEditor
                    attributes={attrForm.moduleAttributes}
                    values={attrValues}
                    errors={attrErrors}
                    onValueChange={(defId, value) => {
                      setAttrValues((prev) => ({ ...prev, [defId]: value }));
                      setAttrErrors((prev) => ({ ...prev, [defId]: '' }));
                    }}
                  />
                </div>
              </div>
            )}

            {/* Custom attributes — user-created; general ones always, category
                ones appear automatically when the matching category is chosen */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('attribute.title')}</h2>
              </div>
              <div className="ims-card-body">
                {hasHiddenValues && (
                  <p className="ims-form-hint" style={{ marginTop: 0 }}>
                    {t('attribute.hiddenValuesNotice')}
                  </p>
                )}
                <ProductAttributesEditor
                  attributes={visibleCustomAttributes}
                  values={attrValues}
                  errors={attrErrors}
                  onValueChange={(defId, value) => {
                    setAttrValues((prev) => ({ ...prev, [defId]: value }));
                    setAttrErrors((prev) => ({ ...prev, [defId]: '' }));
                  }}
                  emptyText={t('attribute.noCustomAttributes')}
                />
              </div>
            </div>
          </div>

          {/* RIGHT — Product Info + Related Items */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('product.information')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('product.productId')}</span>
                  <span className="ims-summary-value">{product.id}</span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('product.sku')}</span>
                  <span className="ims-summary-value"><code>{product.sku}</code></span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('product.createdDate')}</span>
                  <span className="ims-summary-value">
                    {new Date(product.createdAt).toLocaleDateString(langCode)}
                  </span>
                </div>
                {product.modifiedAt && (
                  <div className="ims-summary-row">
                    <span className="ims-summary-label">{t('product.lastModified')}</span>
                    <span className="ims-summary-value">
                      {new Date(product.modifiedAt).toLocaleDateString(langCode)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('product.relatedItems')}</h3>
              </div>
              <div className="ims-card-body">
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => hasPurchaseItem
                    ? navigate(`/purchase-items?productId=${product.id}`)
                    : navigate('/purchase-items/create', { state: { productId: product.id, productName: product.name } })}
                  style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}
                >
                  <ShoppingCart size={15} strokeWidth={1.75} />
                  {hasPurchaseItem === false ? t('purchaseItem.createTitle', 'Create Purchase Item') : t('purchaseItem.title')}
                </button>
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => hasSalesItem
                    ? navigate(`/sales-items?productId=${product.id}`)
                    : navigate('/sales-items/create', { state: { productId: product.id, productName: product.name } })}
                  style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}
                >
                  <ShoppingBag size={15} strokeWidth={1.75} />
                  {hasSalesItem === false ? t('salesItem.createTitle', 'Create Sales Item') : t('salesItem.title')}
                </button>
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => navigate('/products/categories')}
                  style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}
                >
                  <Tag size={15} strokeWidth={1.75} />
                  {t('productCategory.title')}
                </button>
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => navigate('/products/units')}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <Ruler size={15} strokeWidth={1.75} />
                  {t('unit.title')}
                </button>
              </div>
            </div>
          </div>

        </div>
      </form>
    </Layout>
  );
};

export default ProductEditPage;
